import { Injectable } from '@angular/core';
import { supabase } from './supabase.client';
import { PostgrestResponse, PostgrestSingleResponse } from '@supabase/supabase-js';
import { Contestant, ContestantExt } from '../shared/models/auth.models';
import { TABLE_NAMES, VIEW_NAMES } from './supabase.constants';
import { fromApi, toApi, toSnakeCase } from '../shared/utils/case-convert';
import { ContestantForAdmin } from '../shared/models/contestants.models';

@Injectable({
  providedIn: 'root'
})
export class SupabaseDbContestantsService {
  public async getList(filter?: Partial<Contestant>): Promise<PostgrestResponse<Contestant>> {
    const q = supabase
      .from(TABLE_NAMES.CONTESTANTS)
      .select('*');

    if (filter) {
      q.match(filter);
    }
    return q;
  }

  public async getListExt(filter?: Partial<ContestantExt>): Promise<PostgrestResponse<ContestantExt>> {
    const q = supabase
      .from(VIEW_NAMES.CONTESTANTS_EXT)
      .select('*');

    if (filter) {
      q.match(filter);
    }
    return q;
  }

  public async getListExtWithFiltersForAdmin(
    page: number,
    pageSize: number,
    sortField: string,
    sortOrder: 'asc' | 'desc' | '',
    projectsYear: number,
    companyName?: string,
    firstName?: string,
    lastName?: string,
    email?: string,
    states?: string[],
    location?: string,
    categories?: string[],
  ): Promise<{ data: ContestantForAdmin[]; count: number }> {
    // Only projects of the given contest year are embedded; contestants without such projects stay in the list.
    let q = supabase
      .from(VIEW_NAMES.CONTESTANTS_EXT)
      .select(`
      *,
      projects (
        state,
        category:categories!inner(name, year)
      )
    `, {count: 'exact'})
      .eq('projects.category.year', projectsYear);

    if (companyName?.trim()) {
      q = q.ilike('company_name', `%${companyName.trim()}%`);
    }
    if (firstName?.trim()) {
      q = q.ilike('first_name', `%${firstName.trim()}%`);
    }
    if (lastName?.trim()) {
      q = q.ilike('last_name', `%${lastName.trim()}%`);
    }
    if (email?.trim()) {
      q = q.ilike('email', `%${email.trim()}%`);
    }
    if (states?.length) {
      q = q.in('state', states);
    }
    if (location?.trim()) {
      q = q.ilike('location', `%${location.trim()}%`);
    }
    if (categories?.length) {
      q = q.in('category', categories);
    }

    const sortBy: string = toSnakeCase(sortField);
    const isAsc: boolean = (sortOrder === 'asc');

    switch (sortBy) {
      case 'first_name':
        q = q.order('first_name', {ascending: isAsc});
        q = q.order('last_name', {ascending: isAsc});
        break;

      case 'last_name':
        q = q.order('last_name', {ascending: isAsc});
        q = q.order('first_name', {ascending: isAsc});
        break;

      default:
        if (sortBy) {
          q = q.order(sortBy, {ascending: isAsc});
        }
        break;
    }

    if (sortBy !== 'created_at') {
      q = q.order('created_at', {ascending: false});
    }

    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    q = q.range(from, to);

    const {data, count, error} = await q;

    if (error) throw error;

    return {data: fromApi<ContestantForAdmin[]>(data) ?? [], count: count ?? 0};
  }

  public async getById(id: string): Promise<PostgrestSingleResponse<Contestant>> {
    return supabase
      .from(TABLE_NAMES.CONTESTANTS)
      .select('*')
      .eq('id', id)
      .single();
  }

  public async getExtById(id: string): Promise<PostgrestSingleResponse<ContestantExt>> {
    return supabase
      .from(VIEW_NAMES.CONTESTANTS_EXT)
      .select('*')
      .eq('id', id)
      .single();
  }

  public async getExtByIdForAdmin(id: string, projectsYear: number): Promise<PostgrestSingleResponse<ContestantForAdmin>> {
    return supabase
      .from(VIEW_NAMES.CONTESTANTS_EXT)
      .select(`
      *,
      projects (
        state,
        category:categories!inner(name, year)
      )
    `)
      .eq('id', id)
      .eq('projects.category.year', projectsYear)
      .single();
  }

  public async getByEmail(email: string, onlyEmail = false): Promise<PostgrestSingleResponse<Contestant | null>> {
    return supabase
      .from(TABLE_NAMES.CONTESTANTS)
      .select(onlyEmail ? 'email' : '*')
      .eq('email', email.toLowerCase())
      .maybeSingle();
  }

  public async upsert(id: string, upsertData: Partial<Contestant>): Promise<PostgrestSingleResponse<Contestant>> {
    return supabase
      .from(TABLE_NAMES.CONTESTANTS)
      .upsert(toApi(upsertData))
      .eq('id', id)
      .select()
      .single();
  }

  public async update(id: string, updateData: Partial<Contestant>): Promise<PostgrestSingleResponse<Contestant>> {
    return supabase
      .from(TABLE_NAMES.CONTESTANTS)
      .update(toApi(updateData))
      .eq('id', id)
      .select()
      .single();
  }

  public async delete(id: string): Promise<PostgrestSingleResponse<null>> {
    return supabase
      .from(TABLE_NAMES.CONTESTANTS)
      .delete()
      .eq('id', id)
      .select()
      .single();
  }
}
